import { useState, useEffect } from "react";
import api from "../../utils/api";
import { uploadImageFile } from "../../utils/imageUtils";
import { INP, LBL, S, THEME, TblRow, TblCell, Pill, actBtn, tblHead, newsStatusPill, ImageUploadField, useIsMobile } from "./AdminComponents";
import { useLanguage } from "../../context/LanguageContext";

const { G, G3, O, O2 } = THEME;


const NEWS_STATUS = ["pending", "published", "rejected"];
const NEWS_STATUS_LABELS = {
  ne: { pending: "विचाराधीन", published: "प्रकाशित", rejected: "अस्वीकृत" },
  en: { pending: "Pending",   published: "Published", rejected: "Rejected" },
};

const EMPTY_NEWS_FORM = {
  title: "", excerpt: "", content: "", sector: "",
  imageUrl: "", featured: false, status: "published",
};

const CONTENT = {
  ne: {
    statTotal:         "कुल समाचार",
    statPublished:     "प्रकाशित",
    statPending:       "विचाराधीन",
    statFeatured:      "विशेष",
    deltaSoFar:        "अहिलेसम्म",
    deltaLive:         "लाइभ",
    deltaReview:       "समीक्षा",
    deltaFeatured:     "फिचर्ड",
    searchPlaceholder: "🔍 समाचार खोज्नुहोस्…",
    filterAllSector:   "सबै क्षेत्र",
    filterAllStatus:   "सबै स्थिति",
    refresh:           "↻ ताजा गर्नुहोस्",
    addBtn:            "+ समाचार थप्नुहोस्",
    colTitle:          "शीर्षक",
    colSector:         "क्षेत्र",
    colStatus:         "स्थिति",
    colDate:           "मिति",
    colActions:        "कार्य",
    loading:           "समाचार लोड हुँदैछ…",
    empty:             "कुनै समाचार भेटिएन।",
    approve:           "✓ स्वीकृत",
    rejectPrompt:      "अस्वीकृत कारण:",
    reject:            "✗ अस्वीकृत",
    reapprove:         "✓ पुनः स्वीकृत",
    edit:              "सम्पादन",
    delete:            "मेटाउनुहोस्",
    modalAdd:          "➕ समाचार थप्नुहोस्",
    modalEdit:         "✏️ समाचार सम्पादन",
    lblTitle:          "शीर्षक *",
    lblExcerpt:        "संक्षिप्त विवरण *",
    lblContent:        "विस्तृत सामग्री *",
    lblSector:         "क्षेत्र",
    selectSector:      "क्षेत्र छान्नुहोस्",
    lblStatus:         "स्थिति",
    lblImage:          "छवि",
    featuredCheckbox:  "विशेष समाचार (फिचर्ड)",
    saveBtn:           "💾 सुरक्षित गर्नुहोस्",
    savingBtn:         "सुरक्षित हुँदैछ…",
    cancel:            "रद्द गर्नुहोस्",
    deleteConfirm:     (title) => <>के तपाईं <strong>"{title}"</strong> मेटाउन निश्चित हुनुहुन्छ?</>,
    deleteYes:         "हो, मेटाउनुहोस्",
    errLoad:           "समाचार लोड गर्न असफल भयो।",
    errSave:           "सुरक्षित गर्न असफल भयो।",
    errDelete:         "मेटाउन असफल भयो।",
    errStatus:         "स्थिति अपडेट गर्न असफल भयो।",
    locale:            "ne-NP",
  },
  en: {
    statTotal:         "Total News",
    statPublished:     "Published",
    statPending:       "Pending",
    statFeatured:      "Featured",
    deltaSoFar:        "So far",
    deltaLive:         "Live",
    deltaReview:       "Review",
    deltaFeatured:     "Featured",
    searchPlaceholder: "🔍 Search news…",
    filterAllSector:   "All Sectors",
    filterAllStatus:   "All Statuses",
    refresh:           "↻ Refresh",
    addBtn:            "+ Add News",
    colTitle:          "Title",
    colSector:         "Sector",
    colStatus:         "Status",
    colDate:           "Date",
    colActions:        "Actions",
    loading:           "Loading news…",
    empty:             "No news found.",
    approve:           "✓ Approve",
    rejectPrompt:      "Rejection reason:",
    reject:            "✗ Reject",
    reapprove:         "✓ Re-approve",
    edit:              "Edit",
    delete:            "Delete",
    modalAdd:          "➕ Add News",
    modalEdit:         "✏️ Edit News",
    lblTitle:          "Title *",
    lblExcerpt:        "Excerpt *",
    lblContent:        "Content *",
    lblSector:         "Sector",
    selectSector:      "Select a sector",
    lblStatus:         "Status",
    lblImage:          "Image",
    featuredCheckbox:  "Featured News",
    saveBtn:           "💾 Save",
    savingBtn:         "Saving…",
    cancel:            "Cancel",
    deleteConfirm:     (title) => <>Are you sure you want to delete <strong>"{title}"</strong>?</>,
    deleteYes:         "Yes, Delete",
    errLoad:           "Failed to load news.",
    errSave:           "Failed to save.",
    errDelete:         "Failed to delete.",
    errStatus:         "Failed to update status.",
    locale:            "en-US",
  },
};

function NewsPage() {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;
  const statusLabels = NEWS_STATUS_LABELS[language] || NEWS_STATUS_LABELS.en;
  const isMobile = useIsMobile();

  const [sectors, setSectors]           = useState([]); // [{ _id, name, order, ... }] from Sectors API
  const [items, setItems]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [search, setSearch]             = useState("");
  const [filterSector, setFilterSector] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [modal, setModal]               = useState(null);
  const [selected, setSelected]         = useState(null);
  const [form, setForm]                 = useState({ ...EMPTY_NEWS_FORM });
  const [saving, setSaving]             = useState(false);

  useEffect(() => { fetchNews(); fetchSectors(); }, []);

  async function fetchSectors() {
    try {
      const res  = await api.get("/sectors");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.sectors ?? []);
      const sorted = [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSectors(sorted);
    } catch (e) {
      console.error("Error fetching sectors:", e);
    }
  }

  async function fetchNews() {
    setLoading(true); setError("");
    try {
      const res  = await api.get("/news/admin/all");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setItems(data);
    } catch (e) {
      setError(e.response?.data?.message || t.errLoad);
    } finally {
      setLoading(false);
    }
  }

  // Sectors now come from the API with a single `name` field (no separate
  // Nepali/English labels), so just show it as stored — same as Events.
  function displaySector(s) {
    return s || "";
  }

  const filtered = items.filter(n => {
    const q = search.toLowerCase();
    const matchSearch = n.title?.toLowerCase().includes(q) || n.excerpt?.toLowerCase().includes(q);
    const matchSector = filterSector === "all" || n.sector === filterSector;
    const matchStatus = filterStatus === "all" || n.status === filterStatus;
    return matchSearch && matchSector && matchStatus;
  });

  const stats = [
    { label: t.statTotal,     value: items.length,                                       icon: "📰", delta: t.deltaSoFar  },
    { label: t.statPublished, value: items.filter(n => n.status === "published").length,  icon: "✅", delta: t.deltaLive   },
    { label: t.statPending,   value: items.filter(n => n.status === "pending").length,    icon: "⏳", delta: t.deltaReview },
    { label: t.statFeatured,  value: items.filter(n => n.featured).length,                icon: "⭐", delta: t.deltaFeatured },
  ];

  function openAdd()   { setForm({ ...EMPTY_NEWS_FORM, status: "published", sector: sectors[0]?.name || "" }); setSelected(null); setModal("add"); }
  function openEdit(n) { setForm({ title: n.title, excerpt: n.excerpt, content: n.content, sector: n.sector || sectors[0]?.name || "", imageUrl: n.imageUrl || "", featured: !!n.featured, status: n.status }); setSelected(n); setModal("edit"); }
  function openDelete(n) { setSelected(n); setModal("delete"); }
  function closeModal() { setModal(null); setSelected(null); }

  async function handleSave() {
    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const imageUrl = await uploadImageFile(form.imageUrl);
      const payload  = { ...form, imageUrl };
      if (modal === "add") {
        const res     = await api.post("/news", payload);
        const created = res.data?.data ?? res.data;
        setItems([created, ...items]);
      } else {
        const res     = await api.put(`/news/${selected._id}`, payload);
        const updated = res.data?.data ?? res.data;
        setItems(items.map(n => n._id === selected._id ? updated : n));
      }
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || t.errSave);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await api.delete(`/news/${selected._id}`);
      setItems(items.filter(n => n._id !== selected._id));
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || t.errDelete);
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusChange(id, newStatus, remarks = '') {
    try {
      const res = await api.put(`/news/${id}/status`, { status: newStatus, remarks });
      const updated = res.data?.data ?? res.data;
      setItems(items.map(n => n._id === id ? updated : n));
    } catch (e) {
      alert(e.response?.data?.message || t.errStatus);
    }
  }

  function fmt(d) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString(t.locale, { day: "numeric", month: "short", year: "numeric" });
  }

  const NEWS_COLS = "2fr 160px 120px 110px 180px";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Stat cards: 4 on desktop, 2 on mobile */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10 }}>
        {stats.map(s => (
          <div key={s.label} style={S.statCard}>
            <div style={S.statIcon}>{s.icon}</div>
            <div>
              <div style={S.statVal}>{s.value}</div>
              <div style={S.statLabel}>{s.label}</div>
              <div style={S.statDelta}>{s.delta}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input placeholder={t.searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)} style={{ ...INP, flex: "1 1 180px", width: "auto", minWidth: 0, maxWidth: isMobile ? "100%" : 220 }} />
          <select value={filterSector} onChange={e => setFilterSector(e.target.value)} style={{ ...INP, width: "auto", minWidth: 150, maxWidth: isMobile ? "100%" : 200 }}>
            <option value="all">{t.filterAllSector}</option>
            {sectors.map((s) => (
              <option key={s._id} value={s.name}>{s.name}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...INP, width: "auto", minWidth: 110 }}>
            <option value="all">{t.filterAllStatus}</option>
            {NEWS_STATUS.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
          </select>
          <button onClick={fetchNews} style={{ marginLeft: isMobile ? 0 : "auto", padding: "7px 14px", borderRadius: 7, background: G3, border: `1px solid ${G}`, color: G, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t.refresh}</button>
          <button onClick={openAdd} style={{ padding: "7px 16px", borderRadius: 7, background: G, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.addBtn}</button>
        </div>
      </div>

      {error && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 16px", color: "#ef4444", fontSize: 13 }}>⚠ {error}</div>}

      <div style={{ ...S.tableWrap, ...S.tableScroll }}>
        <div style={{ minWidth: isMobile ? 700 : undefined }}>
          <div style={tblHead(NEWS_COLS, isMobile)}>
            <span>{t.colTitle}</span><span>{t.colSector}</span><span>{t.colStatus}</span><span>{t.colDate}</span><span>{t.colActions}</span>
          </div>
          {loading && <div style={{ padding: 30, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.loading}</div>}
          {!loading && filtered.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.empty}</div>}
          {!loading && filtered.map((n, i) => (
            <TblRow key={n._id} cols={NEWS_COLS} isLast={i === filtered.length - 1}>
              <TblCell>
                <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, width: "100%" }}>
                  {n.featured && <span style={{ color: O, flexShrink: 0 }}>⭐</span>}
                  <span style={{ fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", width: "100%" }}>
                    {n.title}
                  </span>
                </div>
              </TblCell>
              <TblCell>
                <span style={{ color: "#6b7280", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", width: "100%" }}>{displaySector(n.sector)}</span>
              </TblCell>
              <TblCell fit>
                <Pill style={newsStatusPill(n.status)}>{statusLabels[n.status] || n.status}</Pill>
              </TblCell>
              <TblCell fit>
                <span style={{ color: "#6b7280", fontSize: 11, whiteSpace: "nowrap" }}>{fmt(n.createdAt)}</span>
              </TblCell>
              <TblCell fit>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {n.status === "pending" && (
                    <>
                      <button onClick={() => handleStatusChange(n._id, "published")} style={{ padding: "5px 11px", borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: "pointer", background: G3, border: `1px solid ${G}`, color: G }}>{t.approve}</button>
                      <button onClick={() => { const r = prompt(t.rejectPrompt); if (r !== null) handleStatusChange(n._id, "rejected", r); }} style={{ padding: "5px 11px", borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: "pointer", background: "rgba(239,68,68,0.07)", border: "1px solid #fecaca", color: "#dc2626" }}>{t.reject}</button>
                    </>
                  )}
                  {n.status === "rejected" && (
                    <button onClick={() => handleStatusChange(n._id, "published")} style={{ padding: "5px 11px", borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: "pointer", background: G3, border: `1px solid ${G}`, color: G }}>{t.reapprove}</button>
                  )}
                  <button onClick={() => openEdit(n)} style={actBtn("edit")}>{t.edit}</button>
                  <button onClick={() => openDelete(n)} style={actBtn("delete")}>{t.delete}</button>
                </div>
              </TblCell>
            </TblRow>
          ))}
        </div>
      </div>

      {(modal === "add" || modal === "edit") && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div style={{ background: "#fff", borderRadius: 14, width: 560, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ background: `linear-gradient(135deg, ${G} 0%, #0a5c2e 100%)`, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>{modal === "add" ? t.modalAdd : t.modalEdit}</span>
              <button onClick={closeModal} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 26, height: 26, borderRadius: "50%", cursor: "pointer" }}>×</button>
            </div>
            <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div><div style={LBL}>{t.lblTitle}</div><input style={INP} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div><div style={LBL}>{t.lblExcerpt}</div><input style={INP} value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} /></div>
              <div><div style={LBL}>{t.lblContent}</div><textarea style={{ ...INP, minHeight: 100, resize: "vertical" }} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} /></div>
              <div>
                <div style={LBL}>{t.lblSector}</div>
                <select style={INP} value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })}>
                  <option value="" disabled>{t.selectSector}</option>
                  {sectors.map((s) => (
                    <option key={s._id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div><div style={LBL}>{t.lblImage}</div><ImageUploadField value={form.imageUrl} onChange={url => setForm({ ...form, imageUrl: url })} /></div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#374151", cursor: "pointer" }}>
                <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} /> {t.featuredCheckbox}
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: 9, borderRadius: 8, background: G, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                  {saving ? t.savingBtn : t.saveBtn}
                </button>
                <button onClick={closeModal} style={{ flex: 1, padding: 9, borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.cancel}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modal === "delete" && selected && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div style={{ background: "#fff", borderRadius: 14, width: 380, maxWidth: "95vw", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 20px" }}>
              <p style={{ fontSize: 13, color: "#374151", margin: 0 }}>{t.deleteConfirm(selected.title)}</p>
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button onClick={handleDelete} disabled={saving} style={{ flex: 1, padding: 9, borderRadius: 8, background: "#ef4444", color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.deleteYes}</button>
                <button onClick={closeModal} style={{ flex: 1, padding: 9, borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.cancel}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsPage;
